import { Dialog } from '@/components/Dialog';
import { reactive, set } from 'vue';
import { useGtm } from '@/composables/useGtm';

const { setDialog: setGtmDialog } = useGtm();

export type DialogProps = {
	-readonly [K in keyof InstanceType<typeof Dialog>['$props']]: InstanceType<typeof Dialog>['$props'][K];
};

type DialogComponent = any | (() => Promise<{ default: any }>);

interface DialogState {
	id: string;
	contentComponent: any;
	props: any;
	dialogOptions: DialogProps;
	visible: boolean;
}

const dialogs = reactive<Record<string, DialogState>>({});
const registry: Record<string, DialogComponent> = {};

export function useDialog() {
	function register(components: Record<string, DialogComponent>) {
		Object.assign(registry, components);
	}

	async function open<T extends Record<string, any> | null>(id: string, props: T, dialogOptions?: DialogProps): Promise<DialogState> {
		const contentComponent = registry[id];
		if (!contentComponent) {
			console.warn(`[useDialog] Dialog "${id}" is not registered.`);
			Promise.reject(`[useDialog] Dialog "${id}" is not registered.`)
		}

		// Resolve dynamic import if needed
		let resolvedContent: any;
		if (typeof contentComponent === 'function') {
			try {
				const mod = await contentComponent();
				resolvedContent = mod.default || mod;
			} catch (err) {
				console.error(`[useDialog] Failed to load dialog "${id}":`, err);
				Promise.reject(err);
			}
		} else {
			resolvedContent = contentComponent;
		}

		set(dialogs, id, {
			id,
			contentComponent: resolvedContent,
			props,
			dialogOptions,
			visible: true,
		});

		setGtmDialog(id);

		return Promise.resolve(dialogs[id]);
	}

	function close(id: string) {
		if (dialogs[id]) dialogs[id].visible = false;
	}

	return {
		dialogs,
		register,
		open,
		close,
	};
}
